// Detecta degradação de conectividade do cliente para os endpoints próprios.
//
// Contexto (caso Tarsila, 17/05): a paciente teve `Failed to fetch` em todos
// os requests para `consultamais.app/*` enquanto outras pacientes na mesma
// janela completavam fluxo normalmente. O ConsultaChat exibiu a tela genérica
// "Estamos com dificuldade para conectar ao chat", sem orientar a paciente
// sobre o que tentar — ela ficou clicando "Tentar novamente" por 12 minutos
// e abriu suporte achando que era bug do app.
//
// Este módulo conta falhas de rede para domínios próprios numa janela móvel.
// Quando 3+ falhas acontecem em ≤30s SEM nenhum sucesso intermediário, marca
// o estado como degradado e notifica listeners. O ConsultaChat usa esse sinal
// para trocar a copy genérica por instruções específicas de rede.

const WINDOW_MS = 30_000;
const THRESHOLD = 3;

// Hosts considerados "próprios" — falhas neles são sintoma de rede do paciente
// bloqueando nosso domínio, não de problema com terceiros (Google Analytics,
// Stream Chat, MercadoPago etc., que têm seus próprios caminhos de retry).
const OWN_HOST_RE = /(?:^|\.)(?:consultamais\.app|consultamaissimplificada\.web\.app|consultamaissimplificada\.firebaseapp\.com|cloudfunctions\.net|firebase\.googleapis\.com)$/i;

const isOwnEndpoint = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return OWN_HOST_RE.test(u.hostname);
  } catch {
    return false;
  }
};

// Estado interno
let failTimestamps = [];
let lastSuccessAt = 0;
let degraded = false;
let degradedSince = 0;
const listeners = new Set();

const pruneOldFails = (now) => {
  failTimestamps = failTimestamps.filter((t) => now - t <= WINDOW_MS);
};

const buildSnapshot = () => ({
  degraded,
  degradedSince,
  recentFailures: failTimestamps.length,
  lastSuccessAt,
});

const notify = () => {
  const snapshot = buildSnapshot();
  for (const fn of listeners) {
    try { fn(snapshot); } catch {}
  }
};

const setDegraded = (next) => {
  if (next === degraded) return;
  degraded = next;
  degradedSince = next ? Date.now() : 0;
  notify();
};

/** Registra uma falha de rede observada pelo patchFetch. */
export const recordFailure = (url) => {
  if (!isOwnEndpoint(url)) return;
  const now = Date.now();
  pruneOldFails(now);
  failTimestamps.push(now);
  if (failTimestamps.length >= THRESHOLD && !degraded) {
    setDegraded(true);
  }
};

/** Registra um sucesso — limpa contadores e (se aplicável) sai do estado degradado. */
export const recordSuccess = (url) => {
  if (!isOwnEndpoint(url)) return;
  lastSuccessAt = Date.now();
  failTimestamps = [];
  if (degraded) setDegraded(false);
};

export const getState = () => buildSnapshot();

export const isDegraded = () => degraded;

/** Inscreve um listener. Retorna função para desinscrever. Dispara imediatamente. */
export const subscribe = (fn) => {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  try { fn(buildSnapshot()); } catch {}
  return () => { listeners.delete(fn); };
};
